import * as yup from 'yup';
import { ISSUE_ATTRIBUTE_NAMES } from '../../constants/issue-attribute-names';
import { CustomAttribute } from '../../sdk/types/common/customAttribute';
import {
  CONSTANT_MANDATORY_FIELDS,
  ManageFieldsMap,
  isCustomField,
} from '../../hooks/useManageFields';

/**
 * Standard field names whose Formik value is an array of objects (pickers /
 * multi-selects). They need `yup.array().min(1, ...)` rather than
 * `yup.string().required(...)`.
 */
const ARRAY_FIELDS = new Set<string>([
  ISSUE_ATTRIBUTE_NAMES.DISCIPLINE,
  ISSUE_ATTRIBUTE_NAMES.CATEGORY,
  ISSUE_ATTRIBUTE_NAMES.LOCATION_IDS,
  ISSUE_ATTRIBUTE_NAMES.CO_REVIEWERS,
  ISSUE_ATTRIBUTE_NAMES.WATCHERS,
]);

/**
 * Standard field names rendered as enum dropdowns. Their Formik value is a
 * string (the chosen enum value) or null/empty when unset.
 */
const ENUM_FIELDS = new Set<string>([
  ISSUE_ATTRIBUTE_NAMES.PRIORITY,
  ISSUE_ATTRIBUTE_NAMES.COST,
  ISSUE_ATTRIBUTE_NAMES.SCHEDULE,
]);

// Applies to both standard and custom fields — the discriminant
// (`fieldName` vs `fieldUuid`) is checked separately below.
const isMandatoryField = (field: { isMandatory?: boolean } | undefined): boolean =>
  field?.isMandatory === true;

const ruleForStandardField = (fieldName: string, requiredMessage: string) => {
  if (ARRAY_FIELDS.has(fieldName)) {
    return yup.array().min(1, requiredMessage).required(requiredMessage);
  }
  if (ENUM_FIELDS.has(fieldName)) {
    return yup.string().nullable().required(requiredMessage);
  }
  // Default: string-typed standard fields (locationDescription, reference,
  // suggestedAnswer, question, dueDate, ...).
  return yup.string().required(requiredMessage);
};

const isCustomAttributeValueEmpty = (values: Array<string | null> | undefined): boolean => {
  if (!values || values.length === 0) return true;
  // Custom attributes serialize an empty input as either an empty array or an
  // array whose items are all falsy.
  return values.every((v) => v == null || v === '');
};

export type BuildManageFieldsValidationParams = {
  manageFieldsMap: ManageFieldsMap;
  customAttributesDefs: CustomAttribute[] | undefined;
  translate: (key: string) => string;
  isFFEnabled: boolean;
};

/**
 * Returns Yup `object().shape(...)` additions for the new mandatory-by-type
 * concept introduced by `manageFields[].isMandatory` on the RFI type.
 *
 * - Returns `{}` when the FF is off — caller can spread the result safely.
 * - Skips the five `CONSTANT_MANDATORY_FIELDS` — required-by-status pipeline
 *   keeps authority over them.
 * - For custom attributes, emits a single `customAttributes` rule using
 *   `yup.array().test(...)` because all custom attrs share one Formik key.
 *
 * Uses dynamic Yup composition; see SCPJM-36295 for full trade-off analysis.
 */
export const buildManageFieldsValidation = ({
  manageFieldsMap,
  customAttributesDefs,
  translate,
  isFFEnabled,
}: BuildManageFieldsValidationParams): Record<string, yup.AnySchema> => {
  if (!isFFEnabled) return {};

  const requiredMessage = translate('new.fieldRequired');
  const shape: Record<string, yup.AnySchema> = {};

  const mandatoryCustomUuids = new Set<string>();

  Object.entries(manageFieldsMap).forEach(([key, field]) => {
    if (!isMandatoryField(field)) return;
    // Skip the five core fields — required-by-status owns them.
    if (CONSTANT_MANDATORY_FIELDS.includes(key)) return;
    // Defensive — a hidden field cannot also be required. The API typically
    // doesn't emit `{ isVisible: false, isMandatory: true }`, but if it did,
    // the field is not on screen so we cannot validate it. Applies to both
    // standard fields and custom attributes.
    if (field.isVisible === false) return;
    if (isCustomField(field)) {
      mandatoryCustomUuids.add(key);
      return;
    }
    shape[key] = ruleForStandardField(key, requiredMessage);
  });

  // Single custom-attributes rule: each mandatory uuid must have a non-empty
  // value. Errors surface at `formik.errors.customAttributes` and we expose
  // the per-uuid error message via the test path.
  //
  // Encoding contract — read alongside `getCustomAttributeError` and
  // `CustomAttributesFields.tsx`:
  // Formik's natural shape for an array-of-objects field is
  // `FormikErrors<T>[]`, keyed by **array index**. We need to surface errors
  // keyed by **custom-attribute uuid** (the user-visible field), and the
  // Formik index is unrelated to the uuid (custom attrs may be reordered or
  // sparsely populated). To bridge that gap we JSON-encode a
  // `{ message, missingUuids }` payload and store it as a single string
  // under `formik.errors.customAttributes`. The consumer
  // (`CustomAttributesFields.tsx`) decodes via `getCustomAttributeError`.
  // Anyone touching either side must keep them in lock-step.
  if (mandatoryCustomUuids.size > 0) {
    // `function validate` (not arrow) is required so Yup can bind `this` for
    // `this.createError` — Yup's TestContext is only available on regular
    // function expressions.
    shape[ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES] = yup
      .array()
      .test('manage-fields-mandatory-custom-attributes', requiredMessage, function validate(value) {
        const arr = (value || []) as Array<{
          id: string;
          values: Array<string | null>;
        }>;
        const valuesById = new Map<string, Array<string | null>>();
        arr.forEach((attr) => {
          valuesById.set(attr.id, attr.values);
        });

        const missing: string[] = [];
        mandatoryCustomUuids.forEach((uuid) => {
          // Safety: the uuid must reference a defined custom attribute.
          // Skip when defs haven't loaded yet (undefined) or when the uuid is
          // not in the loaded defs (prevents leaking errors during the initial
          // render before defs resolve, and for attrs that no longer exist on
          // the project).
          if (!customAttributesDefs || !customAttributesDefs.some((d) => d.id === uuid)) {
            return;
          }
          const valuesForUuid = valuesById.get(uuid);
          if (isCustomAttributeValueEmpty(valuesForUuid)) {
            missing.push(uuid);
          }
        });

        if (missing.length === 0) return true;

        // Encode the missing uuids in the message so consumers can recover the
        // full list. The error must be parsed via `getCustomAttributeError`
        // below — surfacing the raw JSON in any UI is a bug. If this error is
        // ever consumed in `abortEarly: false` mode or a generic catch handler,
        // wrap the read in `getCustomAttributeError` to avoid leaking JSON.
        // `path` defaults to `this.path`, no need to set it explicitly.
        return this.createError({
          message: JSON.stringify({
            message: requiredMessage,
            missingUuids: missing,
          }),
        });
      });
  }

  return shape;
};

/**
 * Helper for callers that need the per-attribute error message. Reads the
 * JSON-encoded `{ message, missingUuids }` blob produced by
 * `buildManageFieldsValidation` above and surfaces the message for a single
 * uuid. The encoder/decoder pair is the only sanctioned way to consume
 * `formik.errors.customAttributes` — see the "Encoding contract" comment on
 * the encoder for why the JSON-string shape is used in place of
 * `FormikErrors<T>[]`.
 *
 * Returns `undefined` if the uuid is not in the missing list.
 */
export const getCustomAttributeError = (
  formikErrorForCustomAttributes: string | undefined,
  uuid: string,
  fallbackMessage: string,
): string | undefined => {
  if (!formikErrorForCustomAttributes) return undefined;
  try {
    // Cast is safe: the JSON string is produced by `buildManageFieldsValidation`
    // above (same module) — the shape is library-owned, not external input.
    const parsed = JSON.parse(formikErrorForCustomAttributes) as {
      message?: string;
      missingUuids?: string[];
    };
    if (parsed.missingUuids?.includes(uuid)) {
      return parsed.message ?? fallbackMessage;
    }
    return undefined;
  } catch {
    // Non-JSON error string — fall back to applying it broadly.
    return formikErrorForCustomAttributes;
  }
};
